import { useState, useEffect } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { Slider } from "@/modern/components/ui/slider";
import { Card } from "@/modern/components/ui/card";
import { setFontSize as setFontSizeUtil, getFontSize } from "@/react/context/bryntumContext.jsx";
import { ViewType } from "@/other/constants/OtherKeys.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { toast } from "sonner";
import { ThemeSwitcher } from "@/modern/components/theme-switcher";
import { Palette } from "lucide-react";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const ROW_STYLES = {
  STRIPE: "Stripe Rows",
  GRIDLINES: "Show Gridlines",
  BOTH: "Stripe Rows & Show Gridlines"
} as const;

const FONT_TYPES = {
  REGULAR: "Regular",
  HEAVY: "Heavy"
} as const;

const BoardSettingsDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { editorHandler, userPreferences } = useSwivuBryntumContext();
  const [fontType, setFontType] = useState<string>(FONT_TYPES.REGULAR);
  const [fontSize, setFontSize] = useState<number>(15);
  const [rowHeight, setRowHeight] = useState<string>(ViewType.normal);
  const [rowStyle, setRowStyle] = useState<string>(ROW_STYLES.GRIDLINES);
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.userPreferencesStore,
    subscriberFn: ({ record }) => {
      if (record?.key?.includes?.("fontType", "viewType", "stripeFeature", "gridlines")) {
        setInitialSettings();
      }
    }
  });

  useEffect(() => {
    setInitialSettings();
  }, []);

  const setInitialSettings = () => {
    if (userPreferences) {
      const fontTypeValue = userPreferences.getSetting("fontType") || FONT_TYPES.REGULAR;
      setFontType(fontTypeValue);

      const fontSizeValue = getFontSize();
      setFontSize(fontSizeValue);

      const rowHeightValue = userPreferences.getSetting("viewType") || ViewType.normal;
      setRowHeight(rowHeightValue);

      const stripeFeature = userPreferences.getSetting("stripeFeature") === "true";
      const gridlines = userPreferences.getSetting("gridlines") === "true";

      if (stripeFeature && gridlines) {
        setRowStyle(ROW_STYLES.BOTH);
      } else if (stripeFeature) {
        setRowStyle(ROW_STYLES.STRIPE);
      } else {
        setRowStyle(ROW_STYLES.GRIDLINES);
      }
    }
  };

  const handleFontTypeChange = (value: string) => {
    setFontType(value);
    userPreferences?.updateSetting("fontType", value);
  };

  const handleFontSizeChange = (value: number[]) => {
    const newSize = value[0];
    setFontSize(newSize);
  };

  const handleFontSizeCommit = (value: number[]) => {
    const newSize = value[0];
    setFontSizeUtil({ value: newSize });
    userPreferences?.updateSetting("fontSize", String(newSize));

    // Resize all editors to original size after font size change
    if (editorHandler) {
      editorHandler.getEditors().forEach((editor: any) => {
        if (editor.editorRef) {
          editor.editorRef.width = editor.editorRef.config.width;
          editor.editorRef.height = editor.editorRef.config.height;
        }
      });
    }
  };

  const handleRowHeightChange = (value: string) => {
    setRowHeight(value);
    userPreferences?.updateSetting("viewType", value);
  };

  const handleRowStyleChange = (value: string) => {
    if (userPreferences) {
      setRowStyle(value);
      if (value === ROW_STYLES.STRIPE) {
        userPreferences?.updateSetting("stripeFeature", String(true));
        userPreferences?.updateSetting("gridlines", String(false));
      } else if (value === ROW_STYLES.GRIDLINES) {
        userPreferences?.updateSetting("gridlines", String(true));
        userPreferences?.updateSetting("stripeFeature", String(false));
      } else if (value === ROW_STYLES.BOTH) {
        userPreferences?.updateSetting("stripeFeature", String(true));
        userPreferences?.updateSetting("gridlines", String(true));
      }
    } else {
      toast.error("User preferences not found");
      console.error("User preferences not found", userPreferences);
      return;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Appearance Settings Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Appearance</h3>
              <p className="text-xs text-muted-foreground">Customize the visual appearance of your board</p>
            </div>

            <div className="space-y-4">
              {/* Board Theme */}
              <div className="space-y-2">
                <Label htmlFor="board-theme">Board Theme</Label>
                <div className="w-full">
                  <ThemeSwitcher
                    className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
                    dropDownMenuContentClassName="w-[--radix-popper-anchor-width]"
                    triggerButtonContent={(themeName) => (
                      <div className="flex items-center justify-between w-full gap-2">
                        <div className="flex items-center gap-2">
                          <Palette className="h-4 w-4" />
                          <span className="font-medium">{themeName}</span>
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Board Font */}
              <div className="space-y-2">
                <Label htmlFor="board-font">Board Font</Label>
                <Select value={fontType} onValueChange={handleFontTypeChange}>
                  <SelectTrigger id="board-font">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(FONT_TYPES).map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Board Font Size */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="board-font-size">Board Font Size</Label>
                  <span className="text-sm text-muted-foreground font-medium">{fontSize}px</span>
                </div>
                <Slider id="board-font-size" min={11} max={25} step={1} value={[fontSize]} onValueChange={handleFontSizeChange} onValueCommit={handleFontSizeCommit} className="w-full" />
                <p className="text-xs text-muted-foreground">Adjust the font size for better readability (11-25px)</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Layout Settings Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Layout</h3>
              <p className="text-xs text-muted-foreground">Control how content is displayed in the board</p>
            </div>

            <div className="space-y-4">
              {/* Board Row Height */}
              <div className="space-y-2">
                <Label htmlFor="board-row-height">Board Row Height</Label>
                <Select value={rowHeight} onValueChange={handleRowHeightChange}>
                  <SelectTrigger id="board-row-height">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ViewType).map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Board Row Style */}
              <div className="space-y-2">
                <Label htmlFor="board-row-style">Board Row Style</Label>
                <Select value={rowStyle} onValueChange={handleRowStyleChange}>
                  <SelectTrigger id="board-row-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(ROW_STYLES).map((style) => (
                      <SelectItem key={style} value={style}>
                        {style}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BoardSettingsDetail;
